import {
  Control,
  Controls,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ControlStatusItem } from '@trustero/trustero-api-web/lib/model/control_status_pb'
import { Department } from '@trustero/trustero-api-web/lib/model/department_pb'
import {
  PolicyRecord,
  PolicyRecords,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ComplianceFrameworks } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import {
  Framework,
  Frameworks,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import FileSaver from 'file-saver'
import Papa from 'papaparse'
import {
  AuditReadinessRecord,
  AuditReadinessTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import {
  getControlTestName,
  getDateText,
  getReasonText,
  getSingleCheckResultText,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { ResponsibilityOptions } from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { VendorRecord } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  OeControlTestCitationType,
  OeControlTestDetermination,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { ControlExportSnapshot } from '@trustero/trustero-api-web/lib/childreporting/childreporting_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import dayjs from 'dayjs'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { controlStatusTitle } from '../../lib/common/types'
import { NONE_NAME, NO_REQUIREMENTS_INDICATOR } from '../globalConstants'
import { getFrameworkGroups } from '../globalHelpers'
import { ComplianceFrameworkGroup } from '../globalTypes'
import { guardFormulaCell } from '../csv/csv.helpers'
import { exportToXlsx, XLSX_MAX_CELL_LENGTH } from './xlsxExport'

// The company a row's control belongs to. Always emitted, in every controls export, even in an
// account that has no children — a column that appears only sometimes is a column no downstream
// reader can rely on, and the importer has to tolerate it either way.
export const ENTITY_HEADER = 'Entity'

/**
 * The account id behind the Entity name, and the identity the importer actually matches on.
 *
 * The name alone cannot carry identity across time or across a group. Rename the company and a file
 * exported yesterday names an entity that no longer exists, so every one of its own rows reads as
 * somebody else's; give a child the same name as its parent and the two become indistinguishable, so
 * the child's rows import INTO the parent — which is the thing the Entity column exists to stop.
 * A stable id has neither problem, and the name stays for the human reading the file.
 */
export const ENTITY_ID_HEADER = 'Entity ID'

const CONTROLS_CSV_HEADERS = [
  ENTITY_HEADER,
  ENTITY_ID_HEADER,
  'Control ID',
  'Control Name',
  'Objective',
  'Notes',
  'Procedure',
  'Policy',
  'Department',
  'Status',
  'Responsibility',
  'Reason',
  'Vendor',
  'Owner',
  'Assignees',
  'Required Evidence',
  'Test Procedures',
  'Risks',
]

// Link columns, emitted only when the caller supplies a page context to build links from; a link
// column of empty cells is worse than none. Safe to add to a re-importable export: the importer
// declares them optional and prepareControlsUpload never reads them, the same way the framework
// and control-test columns ride along.
const CONTROL_URL_HEADER = 'Control URL'

// The control link sits next to the control it names rather than at the end of a wide row.
const CONTROL_URL_INDEX = CONTROLS_CSV_HEADERS.indexOf('Control Name') + 1

// The header names this export owns. A compliance framework is validated only for being non-empty,
// so one can be named "Status" — or now "Entity" — and land a second column under a name already
// taken.
const RESERVED_HEADERS = new Set(
  [...CONTROLS_CSV_HEADERS, CONTROL_URL_HEADER].map((header) =>
    header.toLowerCase(),
  ),
)

/**
 * What a compliance framework's column is CALLED in the file, which is its name unless that name is
 * already a column's.
 *
 * The two columns are genuinely different and both have to survive — the merge keys on identity for
 * exactly that reason — but a file with the same header twice cannot be re-imported at all:
 * transformHeader rejects a repeated header outright, before a single row is looked at, so the whole
 * upload fails. Disambiguating the framework's is the only spelling that keeps both the data and the
 * round trip.
 *
 * Applied by the importer too, which is what makes the file readable again; the framework's real
 * name stays the column's `output` there, so the value parsers are untouched.
 *
 * Not applied against the control-test column names. One of those is "<test> - Result" and a
 * framework named that is not a case worth the reach.
 */
export const frameworkColumnHeader = (name: string): string =>
  RESERVED_HEADERS.has(name.toLowerCase()) ? `${name} (Framework)` : name

// Header and body rows have to be spliced the same way, so both go through here.
const withControlUrl = (cells: string[], url?: string): string[] =>
  url
    ? [
        ...cells.slice(0, CONTROL_URL_INDEX),
        url,
        ...cells.slice(CONTROL_URL_INDEX),
      ]
    : cells

const nameResult = (name: string) => `${getControlTestName(name)} - Result`
const nameReason = (name: string) => `${getControlTestName(name)} - Reason`
const nameDate = (name: string) =>
  `${getControlTestName(name)} - Date Performed`
const nameDetails = (name: string) => `${getControlTestName(name)} - Details`
// Links to the run that produced the result — the control test show page.
const nameUrl = (name: string) => `${getControlTestName(name)} - URL`

// A cell that lists several values joins them with a comma, and the values themselves routinely
// contain commas — a requirement title, a risk name. The importer splits such a cell on unquoted
// commas, so every entry is quoted here and unquoted there.
const quoteListEntry = (value: string): string => {
  const escaped = value.replace(/"/g, '""')
  return `"${escaped}"`
}

// How the importer resolves a risk back out of a Risks cell.
const riskLabel = (risk: Risk): string =>
  `${risk.getCustomId()} ${risk.getName()}`.trim()

// One quoted entry per linked risk. Snapshot rows arrive already labelled, so this takes the labels
// rather than the risks, and both export paths quote in the same place.
const formatRiskList = (labels: string[]): string =>
  labels.map(quoteListEntry).join(',')

// Test procedures from the new model's repeated test_procedures on the Control,
// formatted to match the backend's ReconstructTestProceduresText.
const formatTestProcedures = (control: Control): string =>
  (control.getSuggestions()?.getTestProceduresList() ?? [])
    .map((tp) => {
      const title = tp.getTitle().trim()
      const body = tp.getBody().trim()
      return title ? `- **${title}:** ${body}` : body
    })
    .filter((line) => line.length > 0)
    .join('\n')

// Suggested/required evidence from the new model (batch-loaded onto the Control).
const formatSuggestedEvidence = (control: Control): string =>
  control.getSuggestions()?.getSuggestedEvidence()?.getBody()?.trim() ?? ''

const showPageLink = (
  pageContext: string,
  modelType: MODEL_TYPE,
  modelId: string,
): string =>
  generatePermalink({
    pageContext,
    modelType,
    modelId,
    // Absolute: the csv is read outside the app, where a relative path means nothing.
    isInternalLink: false,
  })

/**
 * Exactly what the Details cell reads from a cited source, and nothing else.
 *
 * Stated as its own type rather than taking OeControlTestCitation, because the two callers supply
 * different messages: the account's own results arrive as the full artifact proto, and a child's
 * arrive as ControlExportCitation, which carries only these fields — the rest never crosses the
 * account boundary. Naming the read set here is what lets one formatter serve both without the
 * narrower one having to pretend to be the wider one.
 */
type RenderableCitation = {
  citationType: OeControlTestCitationType
  evidenceCaption: string
  relevantDate: string
  findingSummary: string
  // Absent on a child's citation: the parent cannot open another account's evidence, so no link is
  // ever produced for one and the id is not shared.
  evidenceId?: string
}

type RenderableTestResult = {
  determination: OeControlTestDetermination
  testString: string
  reasoning: string
  citationsList: RenderableCitation[]
}

// One cited source: its caption, when it was relevant, a link to it, and what the check made of it.
//
// Evidence absent from evidenceLinkIds has been deleted since the test ran and gets no link — the
// server omits exactly those, matching the audit-scan UI, which renders them "(Deleted)".
const formatCitation = (
  citation: RenderableCitation,
  pageContext: string,
  evidenceLinkIds: Map<string, string>,
): string => {
  const isContext = citation.citationType === OeControlTestCitationType.CONTEXT
  const title = isContext ? 'Organizational Context' : citation.evidenceCaption
  const formattedDate = citation.relevantDate
    ? dayjs(citation.relevantDate).format(DATE_FORMATS.ISO_WITH_TIME)
    : ''

  const lines = [formattedDate ? `${title}  ${formattedDate}` : title]
  const linkId = citation.evidenceId
    ? evidenceLinkIds.get(citation.evidenceId)
    : undefined
  if (linkId && pageContext) {
    lines.push(showPageLink(pageContext, MODEL_TYPE.EVIDENCE, linkId))
  }
  if (citation.findingSummary) {
    lines.push(citation.findingSummary)
  }
  return lines.join('\n')
}

// The latest Operating Effectiveness test, one block per test procedure, mirroring the "copy
// summary and details" text the audit-scan UI produces so the csv reads the same way.
const formatTestDetails = (
  details: RenderableTestResult[],
  pageContext: string,
  evidenceLinkIds: Map<string, string>,
): string =>
  details
    .map((result, index) => {
      const icon =
        result.determination === OeControlTestDetermination.PASS ? '✓' : '✗'
      let text = `${icon} ${index + 1}. ${result.testString}\n${
        result.reasoning
      }`
      if (result.citationsList.length) {
        const citationText = result.citationsList
          .map((citation) =>
            formatCitation(citation, pageContext, evidenceLinkIds),
          )
          .join('\n\n')
        text += `\n\nEvidence and Sources:\n${citationText}`
      }
      return text
    })
    .join('\n\n')

export type ReportExportFormat = 'csv' | 'xlsx'

// Ends a cell the xlsx export cut to fit Excel's cell limit; a long test Details cell is the usual
// one. The controls importer rejects a cell that ends with it, so a cut cell cannot replace the
// control's full text. The CSV export has no such limit.
export const TRUNCATED_CELL_NOTE =
  "… [Cut at Excel's 32,767-character cell limit. Export as CSV for the full text.]"

const fitXlsxCell = (cell: string): string =>
  cell.length > XLSX_MAX_CELL_LENGTH
    ? cell.slice(0, XLSX_MAX_CELL_LENGTH - TRUNCATED_CELL_NOTE.length) +
      TRUNCATED_CELL_NOTE
    : cell

export type ReportTable = {
  headers: string[]
  /**
   * One stable identity per column, parallel to `headers`.
   *
   * Merging on the header TEXT is not safe: a custom compliance framework is validated only for
   * being non-empty, so one can legitimately be named "Status" or "Owner" and collide with a fixed
   * column. Those are two different columns that happen to share a label, and collapsing them let
   * the framework's requirement cell land on top of the control's status — losing the status and
   * the framework column at once. The label stays whatever the user called it; only the merge keys
   * on this.
   */
  keys: string[]
  rows: string[][]
}

// Column identities. The prefix is what keeps a framework named "Status" distinct from the Status
// column, and a framework named after a test column distinct from that test's.
const baseKey = (header: string) => `base:${header}`
const frameworkKey = (name: string) => `framework:${name}`
const isFrameworkKey = (key: string) => key.startsWith('framework:')
const testKey = (header: string) => `test:${header}`

/**
 * Merges report tables into one rectangular table, matching cells by column IDENTITY.
 *
 * Not a format check with a fallback. Framework columns are named per account, so a child's header
 * set will almost never equal its parent's — a strict match would reject nearly every merge and
 * silently drop the child rows it was meant to add. The first table sets the column order, each
 * later table contributes only the columns nobody has yet, and every row is re-indexed into the
 * union with `''` wherever a table has nothing to say.
 */
export const mergeReportTables = (tables: ReportTable[]): ReportTable => {
  const present = tables.filter((table) => table.keys.length > 0)
  if (present.length <= 1) {
    return present[0] ?? { headers: [], keys: [], rows: [] }
  }

  const headers: string[] = []
  const keys: string[] = []
  const indexOf = new Map<string, number>()
  present.forEach((table) =>
    table.keys.forEach((key, column) => {
      if (!indexOf.has(key)) {
        indexOf.set(key, keys.length)
        keys.push(key)
        headers.push(table.headers[column])
      }
    }),
  )

  const rows = present.flatMap((table) =>
    table.rows.map((row) => {
      const merged = new Array<string>(keys.length).fill('')
      table.keys.forEach((key, column) => {
        merged[indexOf.get(key) as number] = row[column] ?? ''
      })
      return merged
    }),
  )
  return { headers, keys, rows }
}

// The data a user may not read, whose columns the file leaves out.
export type WithheldColumns = {
  risks?: boolean
  vendor?: boolean
  // Also takes Department, which is derived from the linked policies: the importer rejects a
  // Department value in a row with no Policy.
  policy?: boolean
  frameworks?: boolean
}

/**
 * Leaves out the columns whose data the user may not read.
 *
 * Out rather than blank. The importer treats a column that is present as authoritative, so a blank
 * cell clears that control's value: re-importing the file, by anyone, would unlink every control's
 * risks, vendor, policies or frameworks. A column that is left out keeps each control's current one.
 *
 * Applied to the merged table, so a child's rows cannot bring a withheld column back with the
 * caller's own cells blank under it.
 */
const withoutColumns = (
  table: ReportTable,
  withheld: WithheldColumns,
): ReportTable => {
  const withheldKeys = new Set(
    [
      ...(withheld.risks ? ['Risks'] : []),
      ...(withheld.vendor ? ['Vendor'] : []),
      ...(withheld.policy ? ['Policy', 'Department'] : []),
    ].map(baseKey),
  )
  const kept = table.keys.map(
    (key) =>
      !withheldKeys.has(key) && !(withheld.frameworks && isFrameworkKey(key)),
  )
  const keep = <T,>(cells: T[]): T[] =>
    cells.filter((_, column) => kept[column])
  return {
    headers: keep(table.headers),
    keys: keep(table.keys),
    rows: table.rows.map(keep),
  }
}

/**
 * Renders one child account's snapshot as report rows.
 *
 * Deliberately shares every naming and formatting helper with the parent's own rows — the test
 * column names, the requirement quoting, the no-requirements indicator, the Details text. Column
 * naming lives in one place so a rename cannot leave a child's values landing in a column of their
 * own; only which FIELDS the child sends is decided on the server, which is the half that needs to
 * be auditable.
 *
 * `includeTests` is false for the plain Control Detail export, mirroring how it passes no
 * `controlCheckData` for the parent's own controls.
 *
 * No link columns and no citation links: both are built from the caller's page context and would
 * resolve to the PARENT's objects, quietly pointing at the wrong control or a run that isn't there.
 */
export const buildChildControlRows = (
  snapshot: ControlExportSnapshot.AsObject,
  includeTests: boolean,
): ReportTable => {
  const extraHeaders: string[] = []
  const extraKeys: string[] = []
  const seen = new Set<string>()
  const addColumn = (key: string, header: string) => {
    if (!seen.has(key)) {
      seen.add(key)
      extraKeys.push(key)
      extraHeaders.push(header)
    }
  }
  const addTest = (header: string) => addColumn(testKey(header), header)

  snapshot.rowsList.forEach((row) => {
    row.frameworksList.forEach((framework) =>
      addColumn(
        frameworkKey(framework.frameworkName),
        frameworkColumnHeader(framework.frameworkName),
      ),
    )
    if (!includeTests) return
    row.testsList.forEach((test) => {
      addTest(nameResult(test.testName))
      addTest(nameReason(test.testName))
      addTest(nameDate(test.testName))
      addTest(nameDetails(test.testName))
    })
  })

  const headers = [...CONTROLS_CSV_HEADERS, ...extraHeaders]
  const keys = [...CONTROLS_CSV_HEADERS.map(baseKey), ...extraKeys]
  const columnOf = new Map(keys.map((key, index) => [key, index]))

  const rows = snapshot.rowsList.map((row) => {
    const cells = new Array<string>(keys.length).fill('')
    const base = [
      snapshot.orgName,
      snapshot.accountId,
      row.controlModelId,
      row.name,
      row.objective,
      row.notes,
      row.procedure,
      row.policiesList.join('|'),
      row.departmentsList.join('|'),
      row.status,
      ResponsibilityOptions[row.responsibility],
      row.notApplicableReason,
      row.vendor,
      row.ownerEmail,
      row.assigneeEmailsList.join(','),
      row.requiredEvidence,
      row.testProcedures,
      formatRiskList(row.risksList),
    ]
    base.forEach((value, index) => {
      cells[index] = value ?? ''
    })

    row.frameworksList.forEach((framework) => {
      const column = columnOf.get(frameworkKey(framework.frameworkName))
      if (column === undefined) return
      cells[column] = framework.requirementNamesList.length
        ? framework.requirementNamesList.map(quoteListEntry).join(',')
        : NO_REQUIREMENTS_INDICATOR
    })

    if (includeTests) {
      row.testsList.forEach((test) => {
        const put = (header: string, value: string) => {
          const column = columnOf.get(testKey(header))
          if (column !== undefined) cells[column] = value
        }
        put(
          nameResult(test.testName),
          getSingleCheckResultText(
            test.result,
            test.testName as CONTROL_TEST_NAMES,
          ),
        )
        put(nameReason(test.testName), getReasonText(test.reason))
        put(nameDate(test.testName), getDateText(test.updatedAt))
        put(
          nameDetails(test.testName),
          formatTestDetails(test.detailsList, '', new Map()),
        )
      })
    }
    return cells
  })

  return { headers, keys, rows }
}

export const buildControlsReportData = (
  controls: Control[],
  policies: Record<string, PolicyRecord>,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Record<string, Department>,
  vendors: Record<string, VendorRecord>,
  frameworkGroups: Record<string, ComplianceFrameworkGroup>,
  risksMap: Record<string, Risk[]>,
  customStatusNamesById: Record<string, string>,
  controlTests?: Record<string, AuditReadinessTestRecord.AsObject[]>,
  pageContext = '',
  evidenceLinkIds: Map<string, string> = new Map(),
  entityName = '',
  entityId = '',
): ReportTable => {
  const sortedCfNames = Object.keys(frameworkGroups).sort(
    (a: string, b: string) => a.localeCompare(b),
  )
  const testHeaders: Set<string> = controlTests
    ? Object.values(controlTests).reduce((set, list) => {
        list.forEach((test) => {
          set.add(nameResult(test.testName))
          if (pageContext) {
            set.add(nameUrl(test.testName))
          }
          set.add(nameReason(test.testName))
          set.add(nameDate(test.testName))
          set.add(nameDetails(test.testName))
        })
        return set
      }, new Set<string>())
    : new Set<string>()

  const updatedHeaders = [
    ...withControlUrl(
      CONTROLS_CSV_HEADERS,
      pageContext ? CONTROL_URL_HEADER : undefined,
    ),
    ...sortedCfNames.map(frameworkColumnHeader),
    ...Array.from(testHeaders),
  ]
  // Parallel to updatedHeaders, and spliced the same way. Two columns may share a header — a custom
  // framework can be named "Status" — so only these tell the merge which is which.
  const updatedKeys = [
    ...withControlUrl(
      CONTROLS_CSV_HEADERS.map(baseKey),
      pageContext ? baseKey(CONTROL_URL_HEADER) : undefined,
    ),
    ...sortedCfNames.map(frameworkKey),
    ...Array.from(testHeaders).map(testKey),
  ]

  const data = controls.map((control: Control) => {
    const department = departments[control.getDepartmentId()]
    const frameworkIds = new Set(control.getFrameworkIdsList())
    const controlId = control.getId()
    const trimmedControlId = controlId.trim()
    const policyNames = controlPolicyNamesByControlId[control.getId()] || []
    const policyNameForCsv =
      policyNames.length > 0
        ? policyNames.join('|')
        : policies[control.getPolicyModelId()]?.getName() || NONE_NAME
    const departmentNames =
      controlDepartmentNamesByControlId[control.getId()] || []
    const departmentNameForCsv =
      departmentNames.length > 0
        ? departmentNames.join('|')
        : department?.getName() || NONE_NAME

    const row = withControlUrl(
      [
        entityName,
        entityId,
        control.getModelId(),
        control.getName().trim(),
        control.getObjective().replace('##', '').trim(),
        control.getNote().trim(),
        (control.getProcedure() ?? '').trim(),
        policyNameForCsv,
        departmentNameForCsv,
        customStatusNamesById[control.getCustomStatusId()] ||
          controlStatusTitle[control.getStatus()],
        ResponsibilityOptions[control.getResponsibility()],
        control.getNotApplicableReason(),
        vendors[control.getVendorId()]?.getName() || '',
        control.getOwnerEmail(),
        control.getAssigneeEmailsList().join(','),
        formatSuggestedEvidence(control),
        formatTestProcedures(control),
        formatRiskList((risksMap[trimmedControlId] ?? []).map(riskLabel)),
      ],
      pageContext
        ? showPageLink(pageContext, MODEL_TYPE.CONTROL, control.getModelId())
        : undefined,
    )

    const linkedCfbIds = new Set(control.getComplianceFrameworkIdsList())
    sortedCfNames.forEach((cfName: string) => {
      const group = frameworkGroups[cfName]
      const frameworks = group.objectives
        .filter((objective: Framework) =>
          frameworkIds.has(objective.getModelId()),
        )
        .map((objective: Framework) => quoteListEntry(objective.getName()))
        .join(',')
      if (frameworks) {
        row.push(frameworks)
      } else if (
        linkedCfbIds.has(group.base.getId()) ||
        linkedCfbIds.has(group.base.getModelId())
      ) {
        // Linked to the framework but no requirement links — emit the indicator
        // so the import re-creates the framework link with no requirements.
        row.push(NO_REQUIREMENTS_INDICATOR)
      } else {
        row.push('')
      }
    })
    const tests =
      controlTests && controlId in controlTests ? controlTests[controlId] : []

    // add columns for each test header
    Array.from(testHeaders).forEach((_) => row.push(''))

    // Located by key, not by header text: a custom framework named after a test column would
    // otherwise be found first and take the test's value.
    const testColumn = (header: string) => updatedKeys.indexOf(testKey(header))

    tests.forEach((check) => {
      const resultIdx = testColumn(nameResult(check.testName))
      const reasonIdx = testColumn(nameReason(check.testName))
      const dateIdx = testColumn(nameDate(check.testName))
      const detailsIdx = testColumn(nameDetails(check.testName))

      row[resultIdx] = getSingleCheckResultText(
        check.result,
        check.testName as CONTROL_TEST_NAMES,
      )
      row[reasonIdx] = getReasonText(check.reason)
      row[dateIdx] = getDateText(check.updatedAt)
      row[detailsIdx] = formatTestDetails(
        check.detailsList,
        pageContext,
        evidenceLinkIds,
      )

      if (pageContext) {
        const urlIdx = testColumn(nameUrl(check.testName))
        // A result carried over from a run that has since been deleted names no run to link to.
        row[urlIdx] = check.auditRunId
          ? showPageLink(pageContext, MODEL_TYPE.AUDITBOT_RUN, check.auditRunId)
          : ''
      }
    })

    return row
  })

  return { headers: updatedHeaders, keys: updatedKeys, rows: data }
}

export const generateControlsReport = async ({
  headers,
  rows,
}: ReportTable): Promise<Blob> => {
  const guardedData = rows.map((row) =>
    row.map((cell) => guardFormulaCell(String(cell ?? ''))),
  )
  // Papa rather than a hand-joined header line: a compliance framework whose
  // NAME contains a comma used to add a header field and shift every column
  // after it.
  //
  // Quote guarded cells so that a reader that strips a leading spacefrom an unquoted
  // field won't silently undo the guard and bring the #NAME? error back
  // The header row needs guarding too: compliance-framework names come from user
  // input (a custom framework is validated only for being non-empty), so one named
  // "-Acme" would open as a formula. normalizeHeader trims on re-import.
  const csvRows = Papa.unparse(
    [headers.map(guardFormulaCell), ...guardedData],
    {
      quotes: (value) => /^\s/.test(String(value ?? '')),
      newline: '\r\n',
    },
  )
  // BOM so Excel decodes as UTF-8 instead of the system code page; without it
  // curly quotes and dashes arrive as mojibake. normalizeHeader strips it on
  // re-import.
  return new Blob(['\uFEFF' + csvRows], { type: 'text/csv;charset=utf-8;' })
}

export const DownloadControlsReport = async (
  controls: Controls | undefined,
  policies: PolicyRecords | undefined,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Department[] | undefined,
  vendors: VendorRecord[] | undefined,
  complianceFrameworks: ComplianceFrameworks | undefined,
  cfObjectives: Frameworks | undefined,
  risks: Risk[] | undefined,
  controlStatuses: ControlStatusItem[] | undefined,
  controlCheckData?: AuditReadinessRecord[],
  // The account (and audit) the links belong to, from the route. Omitted by callers that build
  // links for nothing — without it the link columns and the citation links are left out.
  pageContext?: string,
  evidenceLinkIds?: Map<string, string>,
  format: ReportExportFormat = 'csv',
  // Grouped rather than appended one by one: the positional list above is already long, and these
  // two always travel together — the entity a row belongs to, and the other entities to append.
  entities: {
    // The caller's own company name and account id, for its own rows' Entity columns.
    name?: string
    id?: string
    // Consented child accounts' controls, appended after the caller's own rows. Empty in every
    // account that is not a reporting parent, and inside an audit.
    childSnapshots?: ControlExportSnapshot.AsObject[]
  } = {},
  withheld: WithheldColumns = {},
): Promise<void> => {
  const ctrls = controls?.getItemsList() || []
  const policiesMap = (policies?.getItemsList() || []).reduce(
    (acc: Record<string, PolicyRecord>, policy: PolicyRecord) => {
      acc[policy.getModelId()] = policy
      return acc
    },
    {},
  )
  const deptMap = (departments || []).reduce(
    (acc: Record<string, Department>, dept: Department) => {
      acc[dept.getId()] = dept
      acc[dept.getModelId()] = dept
      return acc
    },
    {},
  )

  const frameworkGroups = getFrameworkGroups(complianceFrameworks, cfObjectives)

  const controlTestsMap = (controlCheckData || []).reduce(
    (
      acc: Record<string, AuditReadinessTestRecord.AsObject[]>,
      record: AuditReadinessRecord,
    ) => {
      acc[record.getControlId()] = record
        .getControlTestsList()
        .map((ele) => ele.toObject())

      return acc
    },
    {},
  )

  const vendorsMap = (vendors || []).reduce(
    (acc: Record<string, VendorRecord>, vendor: VendorRecord) => {
      acc[vendor.getId()] = vendor
      return acc
    },
    {},
  )

  const risksMap = (risks || []).reduce(
    (acc: Record<string, Risk[]>, risk: Risk) => {
      risk
        .getControlIds()
        .split(',')
        .forEach((controlId) => {
          const trimmedControlId = controlId.trim()
          if (!acc[trimmedControlId]) {
            acc[trimmedControlId] = []
          }
          acc[trimmedControlId].push(risk)
        })
      return acc
    },
    {},
  )

  const customStatusNamesById = (controlStatuses || []).reduce(
    (acc: Record<string, string>, status: ControlStatusItem) => {
      acc[status.getId()] = status.getName()
      return acc
    },
    {},
  )

  const baseFileName = controlCheckData
    ? 'controls-with-checks'
    : 'control-detail'

  // The caller's own rows first, so they set the column order and a merged file still opens looking
  // like the file this account has always downloaded. Children only widen it from the right.
  const merged = mergeReportTables([
    buildControlsReportData(
      ctrls,
      policiesMap,
      controlPolicyNamesByControlId,
      controlDepartmentNamesByControlId,
      deptMap,
      vendorsMap,
      frameworkGroups,
      risksMap,
      customStatusNamesById,
      controlTestsMap,
      pageContext,
      evidenceLinkIds,
      entities.name,
      entities.id,
    ),
    // Test columns follow controlCheckData, the same signal the caller's own rows use: the plain
    // Control Detail export passes none and gets none from the children either.
    ...(entities.childSnapshots ?? []).map((snapshot) =>
      buildChildControlRows(snapshot, Boolean(controlCheckData)),
    ),
  ])
  const table = withoutColumns(merged, withheld)

  if (format === 'xlsx') {
    await exportToXlsx(
      `${baseFileName}.xlsx`,
      table.headers,
      table.rows.map((row) => row.map(fitXlsxCell)),
      'Controls',
    )
    return
  }

  const csv = await generateControlsReport(table)
  await FileSaver.saveAs(csv, `${baseFileName}.csv`)
}
